// src/hooks/useAgenda.js
import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import api from '../services/api';
import socket from '../services/socket';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

export function useAgenda() {
    const { user, tieneAccesoGlobal: esAdmin } = useAuth();
    const { showToast } = useToast();

    // Estados de Filtros y Datos
    const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date());
    const [medicos, setMedicos] = useState([]);
    const [medicoSeleccionado, setMedicoSeleccionado] = useState(esAdmin ? 'todos' : (user?.id || ''));
    const [citas, setCitas] = useState([]);

    // Estados de Carga
    const [loading, setLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Estado del Drag & Drop
    const [activeDragCita, setActiveDragCita] = useState(null);

    // 1. CARGA DE DATOS PRINCIPAL
    const fetchAgendaDiaria = useCallback(async (silencioso = false) => {
        try {
            if (!silencioso) setLoading(true);
            const fmtFecha = format(fechaSeleccionada, 'yyyy-MM-dd');
            const res = await api.get(`/consultas/agenda?fecha=${fmtFecha}&profesionalId=${medicoSeleccionado}`);
            setCitas(Array.isArray(res) ? res : []);
        } catch (err) {
            console.error("Error cargando agenda:", err);
            showToast('Error al sincronizar la agenda', 'error');
        } finally {
            setLoading(false);
            setIsInitialLoad(false);
        }
    }, [fechaSeleccionada, medicoSeleccionado, showToast]);

    // 2. CARGA DE FACULTATIVOS
    useEffect(() => {
        if (esAdmin) {
            api.get('/profesionales')
                .then(res => setMedicos((Array.isArray(res) ? res : []).filter(m => m.pasaConsulta !== false)))
                .catch(err => console.error("Error cargando personal:", err));
        } else {
            setMedicos([{ id: user?.id, nombre: user?.nombre || 'Doctor/a', apellidos: user?.apellidos || '', especialidad: user?.especialidad || 'Mi Consulta' }]);
        }
    }, [esAdmin, user]);

    // 3. WEBSOCKETS (Tiempo Real)
    useEffect(() => {
        fetchAgendaDiaria();
        if (!socket.connected) socket.connect();

        const recargarSilencioso = () => fetchAgendaDiaria(true);
        socket.on('nueva_cita_creada', recargarSilencioso);
        socket.on('estado_cita_actualizado', recargarSilencioso);
        socket.on('consulta_actualizada', recargarSilencioso);

        return () => {
            socket.off('nueva_cita_creada', recargarSilencioso);
            socket.off('estado_cita_actualizado', recargarSilencioso);
            socket.off('consulta_actualizada', recargarSilencioso);
        };
    }, [fetchAgendaDiaria]);

    // 4. LÓGICA DE DRAG & DROP
    const handleDragStart = useCallback((event) => {
        const { active } = event;
        const citaEnElAire = citas.find(c => c.id === active.id || c._id === active.id);
        if (citaEnElAire) setActiveDragCita(citaEnElAire);
    }, [citas]);

    const handleDragCancel = useCallback(() => setActiveDragCita(null), []);

    const handleDragEnd = useCallback(async (event) => {
        setActiveDragCita(null);
        const { active, over } = event;
        if (!over) return;

        const citaId = active.id;
        const [nuevaHora, nuevoProfesionalId] = over.id.split('_');

        const citaIndex = citas.findIndex(c => c.id === citaId || c._id === citaId);
        if (citaIndex === -1) return;
        const citaOriginal = citas[citaIndex];

        const profOriginalId = citaOriginal.profesional?.id || citaOriginal.profesional?._id || citaOriginal.profesional;
        if (citaOriginal.hora === nuevaHora && String(profOriginalId) === String(nuevoProfesionalId)) return;

        const [horas, minutos] = nuevaHora.split(':');
        const nuevaFechaStr = new Date(citaOriginal.fecha);
        nuevaFechaStr.setHours(parseInt(horas, 10), parseInt(minutos, 10), 0, 0);

        const ahoraConGracia = new Date();
        ahoraConGracia.setMinutes(ahoraConGracia.getMinutes() - 45);

        if (nuevaFechaStr < ahoraConGracia) {
            showToast('No se puede mover un paciente a un horario pasado.', 'warning');
            return;
        }

        // Actualización Optimista (UX sin latencia)
        const backupCitas = [...citas];
        const citasActualizadas = [...citas];
        citasActualizadas[citaIndex] = {
            ...citaOriginal,
            fecha: nuevaFechaStr.toISOString(),
            hora: nuevaHora,
            profesional: { ...citaOriginal.profesional, id: nuevoProfesionalId, _id: nuevoProfesionalId }
        };
        setCitas(citasActualizadas);

        const movimiento = { fecha: nuevaFechaStr.toISOString(), profesional: nuevoProfesionalId };
        try {
            try {
                await api.put(`/consultas/${citaId}`, movimiento);
            } catch (error) {
                // El paciente ya tiene otra cita a esa hora con otro profesional:
                // igual que al dar la cita, se avisa y se puede mover igualmente.
                if (error.codigo !== 'PACIENTE_CON_CITA') throw error;
                if (!window.confirm(`${error.message}\n\n¿Mover la cita igualmente?`)) {
                    setCitas(backupCitas);
                    return;
                }
                await api.put(`/consultas/${citaId}`, { ...movimiento, confirmarSolapePaciente: true });
            }
            fetchAgendaDiaria(true);
        } catch (error) {
            setCitas(backupCitas); // Reversión en caso de error
            showToast(error.message || 'Error de red: El movimiento ha sido revertido.', 'error');
        }
    }, [citas, showToast, fetchAgendaDiaria]);

    return {
        fechaSeleccionada, setFechaSeleccionada,
        medicos, medicoSeleccionado, setMedicoSeleccionado,
        citas, loading, isInitialLoad,
        activeDragCita,
        handleDragStart, handleDragCancel, handleDragEnd,
        fetchAgendaDiaria,
        esAdmin, user
    };
}